import { Box, Button } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

import VNSelect from '@/components/VNSelect/VNSelect';
import VNTextField from '@/components/VNTextField/VNTextField';
import { useProgress } from '@/contexts/ProgressProvider';
import { useTitle } from '@/contexts/TitleContext';
import VnModalBody from '@/modals/components/VnModalBody';
import VnModalFooter from '@/modals/components/VnModalFooter';
import { useModalContext } from '@/modals/VNModalProvider';
import { type NotificationDto, type NotificationTemplate, TemplateEngine } from '@/models/ExternalNotification';
import { ExternalNotificationService } from '@/services/external-notification-service';
import { getDefaultTemplates } from '@/services/FuelToolsApi/fuelToolsApi.service';
import { applicationDataSelector } from '@/store/slices/application-data';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';
import useAppUtils from '@/utils/appUtils';

import TemplatePreview from './TemplatePreview';
import { validateNotificationTemplate } from './validateWorkflow';

const NotificationTemplateEditor = ({ id, addToList, workflow }) => {
  const { setTitle } = useTitle();
  const [initialized, setInitialized] = useState(false);
  const { loading, setProgress } = useProgress();
  const { ouData, switchOuData } = useSelector(applicationDataSelector);
  const { alert } = useAppUtils();
  const { onClose } = useModalContext();
  const { site, eventTypes, handlerChannelTypes, recipientTypes, placeholders, notificationConfig, templateTypes } = workflow;
  const { permissions, fuelUserAccessData } = useSelector(fuelUserAccessSliceSelector);
  const [placeHoldersData, setPlaceHoldersData] = useState({});

  const methods = useForm<NotificationTemplate>({
    defaultValues: {
      id: null,
      groupId: ouData.ouId,
      name: '',
      template: '',
      engine: TemplateEngine.Handlebars,
    },
  });

  const { control, handleSubmit, setValue, watch, reset } = methods;
  const templateType = watch('templateType');
  const sampleTemplate = useWatch({ control, name: 'tmp.sampleTemplate' });

  const validateNotificationForm = (data: NotificationDto) => {
    const manualErrors = validateNotificationTemplate(data);
    if (manualErrors.errors.length > 0) {
      manualErrors.errors.forEach((error) => {
        console.error(`${error.fieldId}: ${error.message}`);
      });
      const message = `Please fix validation errors before saving: ${manualErrors.errors[0].message}`;
      alert(message, 'error');
      return false;
    }
    return true;
  };

  const onSubmit = async (data: NotificationTemplate) => {
    // console.log('Notification Submitted:', data);
    // const isValid = validateNotificationForm(data);
    // if (!isValid) {
    //   return;
    // }
    try {
      setProgress(true);
      delete data.tmp;
      const response = await ExternalNotificationService.upsertTemplate(data);
      addToList();
      alert('Notification Templated updated.');
    } catch (ex) {
      if (ex?.response?.data) {
        alert(ex.response.data, 'error');
        return;
      }
      const msg =
        ex && ex.message
          ? `${ex.message} Please try again momentarily and contact support if the issue persists.`
          : 'System Error. Please contact support.';
      alert(msg, 'error');
    } finally {
      setTimeout(() => {
        setProgress(false);
      }, 0);
    }
  };

  const init = async () => {
    try {
      setProgress(true);
      const data = {
        OUId: site?.ouId,
        SiteUrl: '',
        SiteTitle: site?.name,
        UserId: fuelUserAccessData?.userId,
        UserEmail: fuelUserAccessData?.email,
      };

      setPlaceHoldersData(data);

      if (id !== 'new') {
        const template = await ExternalNotificationService.getTemplate(id, ouData.ouId);
        reset(template);
      }
      const response = await getDefaultTemplates();
      setValue('tmp.defaultTemplates', response);
      setValue('tmp.placeholders', placeholders);
      setTimeout(() => {
        setInitialized(true);
      }, 0);
    } catch (ex) {
      console.log('Unable to load notification config and details');
    } finally {
      setProgress(false);
    }
  };

  const fetchTemplateContent = async (url) => {
    try {
      setProgress(true);
      const res = await fetch(url);
      if (!res.ok) console.error(`Failed to load file: ${res.status}`);
      const text = await res.text();
      // const formatted = JSON.stringify(JSON.parse(text), null, 4);
      setValue('template', text);
    } catch (ex) {
      console.error(`Failed to load file: ${ex}`);
    } finally {
      setProgress(false);
    }
  };

  useEffect(() => {
    // setTitle(`Push Notification`);
    init();
  }, []);

  useEffect(() => {
    if (sampleTemplate && sampleTemplate !== '') {
      const template = watch('tmp.defaultTemplates').find((t) => t.name === sampleTemplate);
      if (template) {
        fetchTemplateContent(template?.downloadUri);
      }
    }
  }, [sampleTemplate]);

  return (
    <>
      <VnModalBody>
        <div className="notification-template-editor">
          {initialized && (
            <FormProvider {...methods}>
              <form className="form">
                {/* Group Id */}{' '}
                <Box
                  p={2}
                  sx={{
                    '& > *': {
                      mb: 2,
                    },
                    p: 1,
                  }}
                >
                  {/* <VNTextField name="groupId" label="Group ID" disabled /> */}
                  <VNTextField name="name" label="Name" required />
                  {/* <VNTextField name="engine" label="Engine" disabled /> */}
                  <VNSelect name="templateType" label="Template Type" options={templateTypes || []} required />
                  <VNSelect
                    name="tmp.sampleTemplate"
                    label="Sample Template"
                    options={watch('tmp.defaultTemplates')?.map((template: string) => ({
                      label: template.name,
                      value: template.name,
                    }))}
                    // disabled={watch('template') === ''}
                  />

                  {/* Content */}

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      width: '100%',
                      // overflow: 'hidden', // prevents any scrollbars
                    }}
                  >
                    {/* Left Side (Template Input) */}
                    <Box sx={{ flex: '1 1 50%', maxWidth: '50%', minWidth: 0 }}>
                      <VNTextField
                        name="template"
                        label="Template"
                        rows={16}
                        required
                        placeholders={watch('tmp.placeholders') || []}
                      />
                    </Box>

                    {/* Right Side (Template Preview) */}
                    <Box sx={{ flex: '1 1 50%', maxWidth: '50%', minWidth: 0 }}>
                      <TemplatePreview
                        template={watch('template')}
                        data={placeHoldersData}
                        templateType={watch('templateType')}
                      />
                    </Box>
                  </Box>
                </Box>
              </form>
            </FormProvider>
          )}
        </div>
      </VnModalBody>
      <VnModalFooter
        right={
          <>
            <Button variant="contained" sx={{ mr: 1 }} color="success" onClick={handleSubmit(onSubmit)} disabled={loading}>
              Save
            </Button>
            <Button variant="contained" sx={{ mr: 1 }} onClick={() => onClose()}>
              Cancel
            </Button>
          </>
        }
      />
    </>
  );
};

export default NotificationTemplateEditor;
